import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { fmtDateTime } from '../../utils/region';
import { useStore } from '../../store';
import type { ChartLogRow, ParentRef } from '../../utils/api/coalog';

// ── One account's change history (COA-10) ───────────────────────────────────────────────────────
// The drawer behind a chart row's "History" button: every row the chart recorder wrote for this
// account (server/src/db/coaLog.ts), oldest first — who, when, what it was, what it became.
// ⛔ EVERY VALUE IS THE SERVER'S, shown as recorded: codes, names, before / after values, the person
// and the route or script a change came through are data, never translated. Only the labels around
// them are in src/i18n/mt/coalog.ts.
// ⛔ A books login (a client's own people) is refused by the server, 403, and the drawer says so
// WITHOUT calling: a refused call pages the admins on Telegram, and the role is already known here.

const KNOWN_ACTIONS = ['baseline', 'baseline_late', 'create', 'update', 'remove', 'backfill', 'copy', 'override'];
const SNAP_KEYS = ['code', 'name_th', 'name_en', 'class', 'control', 'active', 'deleted'];

/** A recorded value as it is, with '' and null said in words rather than left as nothing.
 *  b13 S2 (B13-14): a PARENT (field parent_id) is named by the code and Thai name the server looked up for it in the
 *  row's own client (`refs`), and '' as the top level — the log keeps the id, and an id on a screen is not a fact a person
 *  can read. An id the server could not name (removed for good, or another client's) prints as recorded. */
export function LogValue({ v, field, refs }: { v: any; field?: string; refs?: Record<string, ParentRef> }) {
  const { t } = useTranslation();
  if (field === 'parent_id') {
    if (v === '' || v === null || v === undefined) return <span data-parent-top style={{ color: 'var(--text-tertiary)' }}>{t('coat_parent_top')}</span>;
    const ref = refs?.[String(v)];
    if (ref) return <span data-parent-ref={ref.code} style={{ color: 'var(--text-primary)' }}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{ref.code}</b> · {ref.nameTh}</span>;
  }
  if (v === '' || v === null || v === undefined) return <span style={{ color: 'var(--text-tertiary)' }}>{t('coal_empty_value')}</span>;
  return <code style={{ fontSize: 11.5, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{String(v)}</code>;
}

/** What one log row says changed: the action, then either "field: before → after" or the whole row. `refs` names the
 *  parents a parent_id row moves between (b13 S2). */
export function ChangeLine({ row, refs }: { row: ChartLogRow; refs?: Record<string, ParentRef> }) {
  const { t } = useTranslation();
  const act = KNOWN_ACTIONS.includes(row.action) ? t(`coal_act_${row.action}`) : row.action;
  return (
    <span data-change={row.action} style={{ display: 'inline' }}>
      <b style={{ fontWeight: 700 }}>{act}</b>
      {row.field ? (
        <span data-field={row.field}>
          {' · '}{t(`coal_field_${row.field}`, { defaultValue: row.field })}: <LogValue v={row.oldValue} field={row.field} refs={refs} /> → <LogValue v={row.newValue} field={row.field} refs={refs} />
        </span>
      ) : row.snapshot ? (
        <span data-snapshot>
          {' · '}{t('coal_snapshot')}{' '}
          {SNAP_KEYS.filter(k => k in (row.snapshot || {})).map((k, i) => (
            <span key={k}>{i ? ' · ' : ''}<LogValue v={row.snapshot![k]} /></span>
          ))}
          {/* (b13 review — B13-14) An account recorded WITH a parent (added under one) names it, by code and name, as a
              move does — never its id. A row with no parent prints exactly as before. */}
          {!!row.snapshot?.parent_id && (
            <span data-snapshot-parent>{' · '}{t('coal_field_parent_id')}: <LogValue v={row.snapshot.parent_id} field="parent_id" refs={refs} /></span>
          )}
        </span>
      ) : null}
    </span>
  );
}

export default function AccountHistory({ account, onClose }: {
  account: { id: string; code: string; name_th: string; name_en?: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const books = useStore(s => s.me?.role) === 'books';
  const [rows, setRows] = useState<ChartLogRow[] | null>(null);
  const [refs, setRefs] = useState<Record<string, ParentRef>>({});
  const [denied, setDenied] = useState(books);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (books) { setDenied(true); return; }
    let alive = true;
    setRows(null); setErr(''); setDenied(false);
    api.acctAccountHistory(account.id)
      .then((d: any) => { if (alive) { setRows(d.history || []); setRefs(d.refs || {}); } })
      .catch((e: any) => { if (!alive) return; if (e?.status === 403) setDenied(true); else setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [account.id, books]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div data-drawer="account-history" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end', background: 'rgba(15, 23, 42, 0.28)' }}>
      <aside role="dialog" aria-modal="true" aria-label={t('coal_history_title', { code: account.code, name: account.name_th })}
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', height: '100%', overflowY: 'auto', background: 'var(--bg-surface)', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 24px rgba(0,0,0,.12)', padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
          <i className="ti ti-history" style={{ fontSize: 18, color: 'var(--accent)', marginTop: 1 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>
            {t('coal_history_title', { code: account.code, name: account.name_th })}
          </div>
          <button onClick={onClose} aria-label={t('coal_history_close')} title={t('coal_history_close')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{t('coal_history_lead')}</div>

        {denied ? (
          <div data-history-denied role="note" style={{ fontSize: 12.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8', borderRadius: 8, padding: '9px 11px' }}>
            <i className="ti ti-lock" aria-hidden /> {t('coal_history_denied')}
          </div>
        ) : err ? (
          <div role="alert" style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderRadius: 8, padding: '9px 11px' }}>{t('coal_history_failed', { error: err })}</div>
        ) : rows === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /> {t('coal_history_loading')}</div>
        ) : rows.length === 0 ? (
          <div data-history-empty style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('coal_history_empty')}</div>
        ) : (
          <ol data-history style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map(r => (
              <li key={r.id} data-log-id={r.id} style={{ padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  <span data-at style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.at)}</span>
                  <span data-who>{t('coal_by', { who: r.actorName })}</span>
                </div>
                <div style={{ marginTop: 2 }}><ChangeLine row={r} refs={refs} /></div>
                {r.reason && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{r.reason}</div>}
                <div data-source style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('coal_via', { source: r.source })}</div>
              </li>
            ))}
          </ol>
        )}
      </aside>
    </div>
  );
}
